import { t } from "../i18n/runtime";
import { useMemo } from "react";
import type { ReactNode } from "react";
import {
  RESOURCES_PER_FACILITY,
  RESOURCES_PER_WEAPON,
  WEAPONS_PER_FACTORY,
} from "@cwb/core";
import type { NationAgentView, NationId } from "@cwb/core";
import type { DynamicProvince, MapProvince } from "@cwb/protocol";
import {
  nationColorCss,
  selectAgent,
  selectMapNation,
  selectNationById,
  useGameStore,
} from "../stores/gameStore";
import type { GameStoreState } from "../stores/gameStore";
import {
  AGENT_STATUS,
  ALLIANCE,
  POLICY_KIND,
  POLICY,
  staleDoctrineText,
  STRATEGIC_MODE,
  formatDoctrineReason,
  translateAgentFailure,
} from "../i18n";
import { NationFlag } from "./NationFlag";
import { Pill, Stat, formatMoney } from "./primitives";
import type { Tone } from "./primitives";

/**
 * The dossier of whichever country the pointer is over.
 *
 * It is docked along the foot of the map, not hung off the cursor. A card at the
 * pointer sat on top of the exact ground being pointed at, which on a board this
 * dense is most of what the player was trying to read; a strip costs the board a
 * margin instead of its middle, and it is see-through so the fronts under it stay
 * legible. Being wide is what pays for that: the same lines fit in two rows of
 * columns rather than a dozen stacked ones.
 *
 * Short is the whole point, so the strip carries what a nation IS and the quiet
 * infrastructure of the exact province under the pointer. Factory, mine and
 * housing counts moved here from the board so only actionable weapons and ore
 * remain permanently visible on every province.
 *
 * Nothing here may carry a `title`: the strip refuses the pointer, so a native
 * tooltip on it would never open. Anything worth saying is said in the row.
 */

const MODE_TONE: Record<string, Tone> = {
  EXPAND: "red",
  DEFEND: "amber",
  RECOVER: "violet",
  TRADE: "cyan",
  SEEK_PEACE: "green",
};

export function kingSourceLabel(
  source: NationAgentView["kingSourceMode"],
): string {
  if (source === "codex") return "CODEX";
  if (source === "local") return "LOCAL";
  return t("NationTooltip.waiting");
}

/** Provinces carry ownership, and a pointer only ever knows about provinces. */
const selectHoveredProvince = (s: GameStoreState): DynamicProvince | null => {
  if (!s.hoveredProvinceId) return null;
  return s.state?.provinces.find((p) => p.id === s.hoveredProvinceId) ?? null;
};

const selectHoveredMapProvince = (s: GameStoreState): MapProvince | null => {
  if (!s.hoveredProvinceId) return null;
  return s.map?.provinces.find((p) => p.id === s.hoveredProvinceId) ?? null;
};

const selectHoveredOwner = (s: GameStoreState): NationId | null => {
  return selectHoveredProvince(s)?.ownerNationId ?? null;
};

type ProvinceInfrastructure = Pick<
  DynamicProvince,
  "weaponFactories" | "resourceFacilities" | "houses"
>;

/** The three figures removed from the map, with labels that make their icons unnecessary. */
export function provinceFacilitySummary(
  province: ProvinceInfrastructure,
): string {
  return t("NationTooltip.weapons_factories_mines_housing", {
    p0: province.weaponFactories,
    p1: province.resourceFacilities,
    p2: province.houses,
  });
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="nation-row">
      <span className="label">{label}</span>
      <span className="nation-row__value">{children}</span>
    </div>
  );
}

interface Industry {
  resourcesPerCycle: number;
  weaponsPerCycle: number;
}

/**
 * What one production cycle will actually yield, by the same rule the core
 * runs: a factory only assembles what the ore on hand can feed, and everything
 * is scaled by the province's supply.
 */
function useIndustry(nationId: NationId | null): Industry {
  const provinces = useGameStore((s) => s.state?.provinces);
  return useMemo<Industry>(() => {
    const industry: Industry = { resourcesPerCycle: 0, weaponsPerCycle: 0 };
    if (!nationId) return industry;
    for (const province of provinces ?? []) {
      if (province.ownerNationId !== nationId) continue;
      industry.resourcesPerCycle +=
        province.resourceFacilities * RESOURCES_PER_FACILITY * province.supply;
      const capacity = province.weaponFactories * WEAPONS_PER_FACTORY;
      if (capacity <= 0) continue;
      const fed = Math.max(0, province.resources) / RESOURCES_PER_WEAPON;
      industry.weaponsPerCycle += Math.min(capacity, fed) * province.supply;
    }
    return industry;
  }, [provinces, nationId]);
}

/**
 * The lines the agent rewrote about itself. A doctrine is a standing text and
 * reads as one; the edit to it is an event, and it is the only place the player
 * watches a model change its own mind.
 *
 * The reason string opens with the nation's name and the strip names it again at
 * the head, so the heading stays bare.
 */
function DoctrineDiff({ agent }: { agent: NationAgentView }) {
  const patch = agent.lastDoctrinePatch;
  if (!patch || (patch.additions.length === 0 && patch.removals.length === 0))
    return null;
  return (
    <div className="diff-card">
      <div className="label">
        {t("NationTooltip.doctrine_updated")}{" "}
        {formatDoctrineReason(patch.reason)}
      </div>
      {patch.additions.map((line, index) => (
        <div className="diff-line diff-line--add" key={`add-${index}`}>
          + {line}
        </div>
      ))}
      {patch.removals.map((line, index) => (
        <div className="diff-line diff-line--remove" key={`rm-${index}`}>
          - {line}
        </div>
      ))}
    </div>
  );
}

export function NationTooltip() {
  const hoveredProvince = useGameStore(selectHoveredProvince);
  const hoveredMapProvince = useGameStore(selectHoveredMapProvince);
  const ownerId = useGameStore(selectHoveredOwner);
  const mapNations = useGameStore((s) => s.map?.nations);
  const nation = useGameStore(selectNationById(ownerId));
  const mapNation = useGameStore(selectMapNation(ownerId));
  const agent = useGameStore(selectAgent(ownerId));
  const industry = useIndustry(ownerId);

  // The hovered province is the only gate now: a docked strip has no placement
  // to compute, so it never needed to know where on the canvas the pointer was.
  if (!nation || !mapNation || !hoveredProvince || !hoveredMapProvince)
    return null;

  const color = nationColorCss(mapNation.color);
  const nameOf = (id: NationId): string =>
    mapNations?.find((n) => n.id === id)?.name ?? id.toUpperCase();

  const stale = agent?.status === "FAILED" || agent?.status === "OFFLINE";
  const allied = nation.alliedWith.filter((id) => id !== nation.id);
  const offered = nation.allianceOffersTo.filter((id) => !allied.includes(id));

  return (
    <div className="nation-tip">
      {/* One line: who it is, how it is playing, and the figures that move
          fastest. Everything here is a fact about the state itself. */}
      <div className="nation-tip__lead">
        <NationFlag nationId={nation.id} height={42} color={color} />
        <span className="nation-tip__name">{mapNation.name}</span>
        <span className="nation-tip__province">
          <span className="label">{t("NationTooltip.province")}</span>{" "}
          {hoveredMapProvince.name}
        </span>
        <Pill tone={MODE_TONE[nation.strategicMode] ?? "neutral"}>
          {STRATEGIC_MODE[nation.strategicMode] ?? nation.strategicMode}
        </Pill>

        <Stat
          label={t("NationTooltip.treasury")}
          value={formatMoney(nation.treasury)}
        />
        <Stat
          label={t("NationTooltip.morale")}
          value={`${Math.round(nation.morale * 100)}%`}
          tone={nation.morale < 0.7 ? "amber" : "neutral"}
        />
        <Stat
          label={t("NationTooltip.province_facilities")}
          value={provinceFacilitySummary(hoveredProvince)}
        />
        <Stat
          label={t("NationTooltip.production_per_cycle")}
          value={
            <>
              {t("NationTooltip.weapons")}{" "}
              <span className="num tone-trade">
                +{industry.weaponsPerCycle.toFixed(1)}
              </span>{" "}
              {t("NationTooltip.resources")}{" "}
              <span className="num tone-alert">
                +{industry.resourcesPerCycle.toFixed(1)}
              </span>
            </>
          }
        />
      </div>

      {/*
       * What the agents decided, in columns. The three short rows take the first
       * line and the three prose rows the second, which is what holds the strip
       * to a fixed two rows however much the king had to say. Each of the prose
       * rows renders its own empty state rather than unmounting, so the columns
       * do not reflow under the pointer as a nation starts and stops talking.
       */}
      <div className="nation-tip__lines">
        {/*
         * Who a nation will not fight is as load-bearing as who it is fighting:
         * an allied border forms no front, so the player reading this has to be
         * able to see why a province that looks exposed is not being attacked.
         */}
        <Row label={ALLIANCE.allied}>
          {allied.length === 0 ? (
            <span className="muted">{ALLIANCE.none}</span>
          ) : (
            <span className="tone-trade">{allied.map(nameOf).join(", ")}</span>
          )}
          {offered.length > 0 ? (
            <span className="muted">
              {" "}
              · {ALLIANCE.offered} {offered.map(nameOf).join(", ")}
            </span>
          ) : null}
        </Row>

        {/*
         * The king's half, above the commander's.
         *
         * 국책 is what was decided and 어전 판단 is why, in the king's own words —
         * and the second one is the entire reason a language model is in this
         * loop rather than a lookup table. A stance the player cannot read is
         * just a number.
         */}
        <Row label={POLICY.label}>
          {nation.policy ? (
            <>
              <span
                className={
                  nation.policy.kind === "LAST_STAND"
                    ? "tone-war"
                    : "tone-trade"
                }
              >
                {POLICY_KIND[nation.policy.kind]}
              </span>
              {nation.policy.targetNationId ? (
                <span className="muted">
                  {" "}
                  · {nameOf(nation.policy.targetNationId)}
                </span>
              ) : null}
            </>
          ) : (
            <span className="muted">{POLICY.none}</span>
          )}
        </Row>

        {/*
         * Whether this nation is still deciding at all.
         *
         * A state whose agent has gone OFFLINE keeps playing its last validated
         * doctrine and stops reacting, and every other figure goes on looking
         * healthy while it does — so the one thing that explains a nation
         * behaving oddly has to be readable here.
         */}
        {/*
          The reason is printed, not hidden behind a `title`.

          It used to be a hover attribute on a pill that is itself inside a
          hover tooltip, which means reaching it required hovering a thing that
          only exists while hovering something else — so in practice the screen
          said "오프라인" and nothing more. A stopped nation and a nation that
          has spent its budget look identical from there, and the second one is
          the player's own doing and fixable.
        */}
        <Row label={t("NationTooltip.agent")}>
          <Pill tone={stale ? "red" : "violet"}>
            {AGENT_STATUS[agent?.status ?? "OFFLINE"]}
          </Pill>
          {agent?.failureReason ? (
            <span className="muted">
              {" "}
              {translateAgentFailure(agent.failureReason)}
            </span>
          ) : null}
          {stale ? <span className="muted"> {staleDoctrineText()}</span> : null}
          {agent ? (
            <span className="muted num">
              {" "}
              · {t("NationTooltip.turn_count", { p0: agent.turnCount })}
            </span>
          ) : null}
        </Row>

        <Row label={t("NationTooltip.royal_decision")}>
          <Pill tone={agent?.kingSourceMode === "codex" ? "cyan" : "neutral"}>
            {kingSourceLabel(agent?.kingSourceMode)}
          </Pill>
        </Row>

        <Row label={POLICY.reading}>
          {nation.policy?.reading ? (
            <span className="quote">{nation.policy.reading}</span>
          ) : (
            <span className="muted">{POLICY.none}</span>
          )}
        </Row>

        <Row label={t("NationTooltip.latest_decision")}>
          {agent?.lastDecisionSummary || (
            <span className="muted">{t("NationTooltip.no_decision_yet")}</span>
          )}
        </Row>

        <Row label={t("NationTooltip.statement")}>
          {agent?.lastPublicStatement ? (
            <span className="quote">
              &ldquo;{agent.lastPublicStatement}&rdquo;
            </span>
          ) : (
            <span className="muted">{t("NationTooltip.no_statement_yet")}</span>
          )}
        </Row>
      </div>

      {agent ? <DoctrineDiff agent={agent} /> : null}
    </div>
  );
}
